// Web Audio multi-track sync engine for the share-web SPA.
//
// Loads every track in the manifest, decodes via the supplied AudioContext, and
// schedules all sources to start at the same `currentTime + headroom` so the
// mix is sample-accurate even after seek. Per-track gain nodes carry
// mute/solo/volume; the master gain feeds destination.
//
// Solo semantics match the desktop app: if ANY track is soloed, only soloed
// tracks are audible; otherwise each track plays at its own volume unless
// muted. Transitions ramp via `setTargetAtTime(…, 0.01)` to avoid clicks.

import type { ShareManifest } from '@bandway/share-types';

export interface TrackState {
  id: string;
  gain: number; // 0..1
  muted: boolean;
  soloed: boolean;
  pan: number;  // TR-2 — [-1, +1], default 0 (center)
}

export interface LoopSection {
  startSec: number;
  endSec: number;
}

/**
 * Pure-function wrap calculator. Returns `startSec` if `currentTime` has
 * reached or crossed `endSec`, else null. Used by the RAF loop tick when a
 * loop is active. Loop wraps only forward — manual seeks outside the range
 * stay untouched until the next forward crossing of `endSec`.
 */
export function computeLoopWrap(
  currentTime: number,
  section: LoopSection | null | undefined,
): number | null {
  if (!section) return null;
  if (currentTime >= section.endSec) return section.startSec;
  return null;
}

export interface PlayerOpts {
  audioContext: AudioContext;
  manifest: ShareManifest;
  /**
   * Resolves a manifest `track.url` to its bytes. The player does NOT know
   * `cdnBase` — the orchestrator (router/page entry) passes a closure that
   * builds the absolute URL (typically
   * `${cdnBase}/shares/${shareId}/${track.url}`) and runs `fetch().arrayBuffer()`.
   * The player just hands `track.url` to this callback as-is.
   */
  fetchAudio: (url: string) => Promise<ArrayBuffer>;
}

export type PlayerEvent =
  | { type: 'load-progress'; trackId: string; current: number; total: number }
  | { type: 'loaded' }
  | { type: 'playing' }
  | { type: 'paused' }
  | { type: 'ended' };

export interface Player {
  load(): Promise<void>;
  play(): Promise<void>;
  pause(): void;
  seek(sec: number): void;
  getPlayhead(): number;
  setTrackState(id: string, state: Partial<TrackState>): void;
  getTrackStates(): Map<string, TrackState>;
  isPlaying(): boolean;
  isLoaded(): boolean;
  /**
   * Returns the current peak level (0..1) for the track's per-track
   * AnalyserNode, computed as max-abs of byte time-domain data normalized
   * around 128. Returns 0 if the track has no analyser (not yet loaded,
   * unknown id, or AnalyserNode unavailable in the host AudioContext).
   */
  getTrackVuLevel(id: string): number;
  /**
   * TR-2: Set the stereo pan for a track. `panValue` in [-1, +1]; values
   * outside the range are clamped. NaN / non-finite → clamped to 0.
   * No-op if the track has no pannerNode (host lacks createStereoPanner).
   */
  setPan(trackId: string, panValue: number): void;
  /**
   * Set the active loop section. While playing, the player polls
   * `getPlayhead()` each animation frame and wraps back to `startSec`
   * when the playhead reaches or crosses `endSec`. Pass `null` to disable
   * looping. Manual seeks outside [startSec, endSec] remain untouched
   * until the next forward crossing of `endSec`.
   */
  setActiveLoopSection(section: LoopSection | null): void;
  subscribe(fn: (event: PlayerEvent) => void): () => void;
}

const RAMP_TIME = 0.01; // 10 ms setTargetAtTime time constant (click-free)
const START_HEADROOM = 0.05; // 50 ms scheduling headroom for sample-accurate start

interface TrackGraph {
  trackId: string;
  buffer: AudioBuffer;
  gainNode: GainNode;
  /**
   * TR-2: StereoPannerNode inserted between gainNode and masterGain for
   * per-track stereo pan. `null` when the host AudioContext doesn't expose
   * `createStereoPanner` (older Safari / test mocks without the stub).
   */
  pannerNode: StereoPannerNode | null;
  /**
   * Per-track AnalyserNode tapped off `gainNode` (post-volume/mute/solo so
   * the meter reflects what's actually audible). `null` when the host
   * AudioContext doesn't expose `createAnalyser` (test mocks).
   */
  analyser: AnalyserNode | null;
  /**
   * Pre-allocated buffer for `getByteTimeDomainData` to avoid per-call
   * allocations in the VU read path. Sized to `analyser.fftSize` (256).
   */
  vuBuffer: Uint8Array | null;
  source: AudioBufferSourceNode | null;
}

export function createPlayer(opts: PlayerOpts): Player {
  const { audioContext: ctx, manifest, fetchAudio } = opts;

  const tracks = new Map<string, TrackGraph>();
  const trackStates = new Map<string, TrackState>();
  const subscribers = new Set<(event: PlayerEvent) => void>();

  let masterGain: GainNode | null = null;
  let loaded = false;
  let playing = false;
  let startTime = 0; // ctx.currentTime when the current play() segment began
  let offsetSec = 0; // playhead offset (seconds into the song)
  let activeLoop: LoopSection | null = null;
  let loopRafHandle: number | null = null;

  function emit(event: PlayerEvent): void {
    for (const fn of subscribers) {
      fn(event);
    }
  }

  function loopTick(): void {
    loopRafHandle = null;
    if (!playing || !activeLoop) return;
    const wrapTo = computeLoopWrap(getPlayhead(), activeLoop);
    if (wrapTo !== null) {
      seek(wrapTo);
    }
    scheduleLoopTick();
  }

  function scheduleLoopTick(): void {
    if (loopRafHandle !== null) return;
    if (typeof requestAnimationFrame === 'function') {
      loopRafHandle = requestAnimationFrame(loopTick);
    }
  }

  function cancelLoopTick(): void {
    if (loopRafHandle !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(loopRafHandle);
    }
    loopRafHandle = null;
  }

  function audibleGainFor(state: TrackState): number {
    // Solo aggregation across all tracks
    let anySoloed = false;
    for (const ts of trackStates.values()) {
      if (ts.soloed) {
        anySoloed = true;
        break;
      }
    }
    if (anySoloed) {
      return state.soloed && !state.muted ? state.gain : 0;
    }
    return state.muted ? 0 : state.gain;
  }

  function applyAllTrackGains(): void {
    const t = ctx.currentTime;
    for (const [id, state] of trackStates) {
      const graph = tracks.get(id);
      if (!graph) continue;
      const target = audibleGainFor(state);
      graph.gainNode.gain.setTargetAtTime(target, t, RAMP_TIME);
    }
  }

  async function load(): Promise<void> {
    if (loaded) return;
    masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    masterGain.connect(ctx.destination);

    const total = manifest.tracks.length;
    for (let i = 0; i < manifest.tracks.length; i++) {
      const track = manifest.tracks[i]!;
      const buf = await fetchAudio(track.url);
      const decoded = await ctx.decodeAudioData(buf);

      const gainNode = ctx.createGain();

      // TR-2: stereo panner between gain and master. Constant-power law per
      // Web Audio spec (matches desktop engine pan-law.js). Guard for older
      // Safari / test hosts that lack createStereoPanner.
      let pannerNode: StereoPannerNode | null = null;
      if (typeof (ctx as { createStereoPanner?: () => StereoPannerNode }).createStereoPanner === 'function') {
        pannerNode = ctx.createStereoPanner();
        pannerNode.pan.value = typeof track.initialPan === 'number' ? track.initialPan : 0;
      }

      const state: TrackState = {
        id: track.id,
        gain: track.initialVolume,
        muted: track.initialMuted,
        soloed: track.initialSoloed,
        pan: typeof track.initialPan === 'number' ? track.initialPan : 0,
      };
      trackStates.set(track.id, state);

      gainNode.gain.value = 0; // will ramp up at play()

      // Routing: gain → panner → master (if panner) ; else gain → master
      if (pannerNode) {
        gainNode.connect(pannerNode);
        pannerNode.connect(masterGain);
      } else {
        gainNode.connect(masterGain);
      }

      // Tap a per-track AnalyserNode off gainNode for the VU meter. Sits
      // *after* the gain node so the meter reflects actual audible level
      // (post mute/solo/volume). Peak is pre-pan (analyser on gainNode, not
      // pannerNode) — pan affects routing/position, not level metering.
      // Connects to nothing further — analysers pull samples from anything
      // routed through them, no extra audio path needed. Some test hosts
      // don't expose `createAnalyser`; skip gracefully so the player still
      // works in those mocks.
      let analyser: AnalyserNode | null = null;
      let vuBuffer: Uint8Array | null = null;
      if (typeof (ctx as { createAnalyser?: () => AnalyserNode }).createAnalyser === 'function') {
        analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        gainNode.connect(analyser);
        vuBuffer = new Uint8Array(analyser.fftSize);
      }

      tracks.set(track.id, {
        trackId: track.id,
        buffer: decoded,
        gainNode,
        pannerNode,                    // TR-2
        analyser,
        vuBuffer,
        source: null,
      });

      emit({ type: 'load-progress', trackId: track.id, current: i + 1, total });
    }

    // Initial gain state (paused) — pre-apply audible levels so the first
    // play() has correct values; sources are not yet started.
    applyAllTrackGains();

    loaded = true;
    emit({ type: 'loaded' });
  }

  async function play(): Promise<void> {
    if (!loaded) {
      throw new Error('player: play() called before load()');
    }
    if (playing) return;

    // Safari needs an explicit resume on user gesture; harmless elsewhere.
    if (ctx.state !== 'running') {
      await ctx.resume();
    }

    const when = ctx.currentTime + START_HEADROOM;

    // Wire ended-detection to the first track (typically the longest / canonical).
    const firstTrackId = manifest.tracks[0]?.id;

    for (const track of manifest.tracks) {
      const graph = tracks.get(track.id);
      if (!graph) continue;
      const source = ctx.createBufferSource();
      source.buffer = graph.buffer;
      source.connect(graph.gainNode);
      if (track.id === firstTrackId) {
        source.onended = () => {
          // Natural end (not pause): pause() calls .stop() which also fires
          // onended, so guard against double-emission.
          if (!playing) return;
          // Loop wrap at natural buffer end. When `activeLoop.endSec` ≥
          // manifest.duration (e.g. CHORUS section reaching the end of the
          // song), the audio source's natural-end fires before the RAF tick
          // can detect the wrap — RAF runs at ~60Hz while the audio clock
          // is sample-accurate. Detect that here and restart at startSec
          // instead of emitting 'ended'.
          if (activeLoop) {
            seek(activeLoop.startSec);
            return;
          }
          playing = false;
          offsetSec = manifest.duration;
          emit({ type: 'ended' });
        };
      }
      source.start(when, offsetSec);
      graph.source = source;
    }

    startTime = when;
    playing = true;
    applyAllTrackGains();
    scheduleLoopTick();
    emit({ type: 'playing' });
  }

  function pause(): void {
    if (!playing) return;

    const elapsed = Math.max(0, ctx.currentTime - startTime);
    offsetSec = Math.min(manifest.duration, offsetSec + elapsed);

    playing = false;
    cancelLoopTick();

    for (const graph of tracks.values()) {
      if (graph.source) {
        graph.source.onended = null;
        try {
          graph.source.stop();
        } catch {
          // already stopped — ignore
        }
        graph.source = null;
      }
    }

    emit({ type: 'paused' });
  }

  function seek(sec: number): void {
    const clamped = Math.max(0, Math.min(manifest.duration, sec));
    if (playing) {
      pause();
      offsetSec = clamped;
      // Fire-and-forget — play() only awaits ctx.resume() which is a no-op
      // when already running (Safari already resumed earlier).
      void play();
    } else {
      offsetSec = clamped;
    }
  }

  function getPlayhead(): number {
    if (playing) {
      return ctx.currentTime - startTime + offsetSec;
    }
    return offsetSec;
  }

  function setTrackState(id: string, partial: Partial<TrackState>): void {
    const current = trackStates.get(id);
    if (!current) return;
    const next: TrackState = {
      ...current,
      ...partial,
      id: current.id, // id is immutable
    };
    trackStates.set(id, next);
    // Solo state changes affect every track's audible gain, so reapply globally.
    applyAllTrackGains();
  }

  function getTrackStates(): Map<string, TrackState> {
    return trackStates;
  }

  function isPlaying(): boolean {
    return playing;
  }

  function isLoaded(): boolean {
    return loaded;
  }

  function getTrackVuLevel(id: string): number {
    const graph = tracks.get(id);
    if (!graph || !graph.analyser || !graph.vuBuffer) return 0;
    graph.analyser.getByteTimeDomainData(graph.vuBuffer);
    let max = 0;
    for (let i = 0; i < graph.vuBuffer.length; i++) {
      // Time-domain bytes are unsigned, centered at 128. Normalize to
      // [-1, 1] then take abs → 0..1 envelope.
      const norm = Math.abs((graph.vuBuffer[i]! - 128) / 128);
      if (norm > max) max = norm;
    }
    return max;
  }

  function subscribe(fn: (event: PlayerEvent) => void): () => void {
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  }

  function setActiveLoopSection(section: LoopSection | null): void {
    activeLoop = section;
    if (playing && section) {
      scheduleLoopTick();
    } else if (!section) {
      cancelLoopTick();
    }
  }

  // TR-2: per-track stereo pan via StereoPannerNode.
  function setPan(trackId: string, panValue: number): void {
    const state = trackStates.get(trackId);
    const graph = tracks.get(trackId);
    if (!state || !graph || !graph.pannerNode) return;
    const clamped = !Number.isFinite(panValue) ? 0
                  : panValue < -1 ? -1
                  : panValue > 1  ?  1
                  : panValue;
    state.pan = clamped;
    graph.pannerNode.pan.setTargetAtTime(clamped, ctx.currentTime, RAMP_TIME);
  }

  return {
    load,
    play,
    pause,
    seek,
    getPlayhead,
    setTrackState,
    getTrackStates,
    isPlaying,
    isLoaded,
    getTrackVuLevel,
    setPan,          // TR-2
    setActiveLoopSection,
    subscribe,
  };
}
